import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Banknote } from "lucide-react";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import { fmtINR, fmtINRCompact } from "@/homeiq/services/format";

export default function RentDiscovery() {
    const {
        rent, setRent,
        landingAffordabilityOutputs,
        landingProfile,
        seedAssessmentFromLanding,
        setActiveSection,
    } = useHomeIQ();
    const [revealed, setRevealed] = useState(false);
    // Landing Reference Profile pipeline: rent → inferred inputs → Core →
    // outputs. Same engines as the assessment, driven by rent so the slider
    // is genuinely responsive. Detailed docs: /homeiq/landing/landing-profile.md
    const homeValue = landingAffordabilityOutputs?.max_affordable_property ?? 0;
    const estimatedEMI = landingAffordabilityOutputs?.emi_on_eligible_loan ?? 0;
    const tenure = landingProfile?.tenure ?? 20;
    const rate = landingProfile?.rate ?? 8.75;

    return (
        <section
            id="rent-discovery"
            data-testid="rent-discovery-section"
            className="relative pt-10 md:pt-12 pb-16 md:pb-20 px-6 md:px-12 bg-white border-t border-[#0A1F44]/5 overflow-hidden"
        >
            <div className="absolute inset-0 blueprint-grid opacity-40 pointer-events-none" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(30,107,255,0.06)_0%,_transparent_60%)] pointer-events-none" />

            <div className="relative max-w-6xl mx-auto">
                {/* Narrative bridge */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="text-center mb-8 md:mb-10"
                >
                    <h2
                        data-testid="hero-subhead"
                        className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#0A1F44] max-w-3xl mx-auto"
                    >
                        Still renting?{" "}
                        <span className="text-[#0A1F44]/55">
                            You may be closer to buying
                        </span>{" "}
                        than you{" "}
                        <span className="text-[#1E6BFF]">think.</span>
                    </h2>

                    <div
                        aria-hidden="true"
                        className="mt-7 mx-auto w-px h-8 bg-gradient-to-b from-[#0A1F44]/20 to-transparent"
                    />

                    <p
                        data-testid="rent-discovery-eyebrow"
                        className="mt-4 text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold"
                    >
                        — Let&apos;s start with one simple question
                    </p>
                    <h3
                        data-testid="rent-discovery-heading"
                        className="mt-3 font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.05] text-[#0A1F44] max-w-2xl mx-auto"
                    >
                        How much rent do you pay{" "}
                        <span className="text-[#1E6BFF]">every month?</span>
                    </h3>
                </motion.div>

                {/* Card */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{
                        duration: 0.9,
                        delay: 0.1,
                        ease: [0.16, 1, 0.3, 1],
                    }}
                    data-testid="rent-discovery-widget"
                    className="relative rounded-3xl bg-white border border-[#0A1F44]/10 shadow-[0_40px_100px_-50px_rgba(10,31,68,0.35)] p-5 md:p-7"
                >
                    {/* Step 1: rent input only */}
                    <div className="rounded-2xl bg-[#F7F8FB] border border-[#0A1F44]/8 p-4 md:p-6">
                        <div className="flex items-baseline justify-between mb-4 gap-3">
                            <label
                                htmlFor="rent-input"
                                className="text-[11px] tracking-[0.2em] uppercase text-[#0A1F44]/55 font-semibold"
                            >
                                Rent (Monthly)
                            </label>
                            <div className="flex items-baseline gap-1 font-display font-extrabold text-3xl md:text-4xl text-[#0A1F44] tabular-nums focus-within:ring-2 focus-within:ring-[#1E6BFF]/30 rounded-md px-1.5 -mx-1.5 transition-all">
                                <span className="text-[#0A1F44]/60">₹</span>
                                <input
                                    id="rent-input"
                                    data-testid="rent-input"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="0"
                                    value={rent == null ? "" : rent.toLocaleString("en-IN")}
                                    onChange={(e) => {
                                        const cleaned = e.target.value.replace(/[^0-9]/g, "");
                                        if (cleaned === "") {
                                            setRent(null);
                                            return;
                                        }
                                        const n = Number(cleaned);
                                        if (!isNaN(n)) {
                                            setRent(Math.min(Math.max(n, 0), 500000));
                                        }
                                    }}
                                    aria-label="Monthly rent in rupees"
                                    className="bg-transparent border-0 outline-none text-right tabular-nums font-display font-extrabold text-3xl md:text-4xl text-[#0A1F44] w-[7.5ch] caret-[#1E6BFF] placeholder:text-[#0A1F44]/25"
                                />
                                <span data-testid="rent-value" className="sr-only">
                                    {rent == null ? "" : rent.toLocaleString("en-IN")}
                                </span>
                            </div>
                        </div>
                        <input
                            id="rent-slider"
                            data-testid="rent-slider"
                            type="range"
                            min="0"
                            max="500000"
                            step="1000"
                            value={rent ?? 0}
                            onChange={(e) => setRent(Number(e.target.value))}
                            className="w-full h-2.5 rounded-full appearance-none cursor-pointer bg-[#0A1F44]/10 accent-[#1E6BFF]
                                [&::-webkit-slider-thumb]:appearance-none
                                [&::-webkit-slider-thumb]:w-6
                                [&::-webkit-slider-thumb]:h-6
                                [&::-webkit-slider-thumb]:rounded-full
                                [&::-webkit-slider-thumb]:bg-[#1E6BFF]
                                [&::-webkit-slider-thumb]:border-4
                                [&::-webkit-slider-thumb]:border-white
                                [&::-webkit-slider-thumb]:shadow-[0_4px_14px_rgba(30,107,255,0.5)]
                                [&::-webkit-slider-thumb]:cursor-grab
                                [&::-moz-range-thumb]:w-6
                                [&::-moz-range-thumb]:h-6
                                [&::-moz-range-thumb]:rounded-full
                                [&::-moz-range-thumb]:bg-[#1E6BFF]
                                [&::-moz-range-thumb]:border-4
                                [&::-moz-range-thumb]:border-white"
                        />
                        <div className="flex justify-between mt-2.5 text-[10px] text-[#0A1F44]/40 tabular-nums tracking-wider">
                            <span>₹0</span>
                            <span>type any amount or drag</span>
                            <span>₹5,00,000</span>
                        </div>
                        {(rent == null || rent === 0) && (
                            <p
                                data-testid="rent-blank-hint"
                                className="mt-3 text-[11px] text-[#0A1F44]/60 italic"
                            >
                                Enter your current monthly rent to personalise projections.
                            </p>
                        )}
                    </div>

                    {/* Primary CTA — toggles to "Continue" once revealed */}
                    {!revealed && (
                        <motion.button
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 }}
                            type="button"
                            onClick={() => setRevealed(true)}
                            disabled={rent == null || rent === 0}
                            data-testid="show-homeiq-cta"
                            className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-[#0A1F44] text-white px-6 py-4 rounded-full text-sm font-semibold hover:bg-[#1E6BFF] transition-colors group disabled:bg-[#0A1F44]/30 disabled:cursor-not-allowed disabled:hover:bg-[#0A1F44]/30"
                        >
                            Show my HomeIQ
                            <span className="transition-transform group-hover:translate-x-1">
                                →
                            </span>
                        </motion.button>
                    )}

                    {/* Step 2: progressive reveal */}
                    <AnimatePresence>
                        {revealed && (
                            <motion.div
                                key="results"
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{
                                    duration: 0.6,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="overflow-hidden"
                            >
                                <div className="pt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Reveal delay={0.1}>
                                        <div
                                            data-testid="insight-home"
                                            className="relative rounded-2xl p-5 border bg-[#0A1F44] border-[#0A1F44] text-white overflow-hidden h-full"
                                        >
                                            <div className="absolute inset-0 blueprint-grid opacity-15 pointer-events-none" />
                                            <Home
                                                className="w-4 h-4 mb-3 text-[#7FA8FF] relative z-10"
                                                strokeWidth={2}
                                            />
                                            <div className="font-display font-extrabold text-3xl md:text-4xl tabular-nums leading-none text-white relative z-10">
                                                {fmtINR(homeValue)}
                                            </div>
                                            <div className="mt-2 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/60 relative z-10">
                                                Home you could afford
                                            </div>
                                        </div>
                                    </Reveal>

                                    <Reveal delay={0.25}>
                                        <div className="rounded-2xl p-5 bg-[#F7F8FB] border border-[#0A1F44]/8 h-full">
                                            <Banknote
                                                className="w-4 h-4 mb-3 text-[#1E6BFF]"
                                                strokeWidth={2}
                                            />
                                            <div className="font-display font-extrabold text-2xl md:text-3xl text-[#0A1F44] tabular-nums leading-none">
                                                {fmtINRCompact(estimatedEMI)}
                                                <span className="text-sm ml-1 text-[#0A1F44]/50 font-semibold">
                                                    /mo
                                                </span>
                                            </div>
                                            <div className="mt-2 text-[10px] tracking-[0.22em] uppercase font-semibold text-[#0A1F44]/50">
                                                Estimated EMI · {tenure} yr · {rate}%
                                            </div>
                                        </div>
                                    </Reveal>
                                </div>

                                <Reveal delay={0.35}>
                                    <p
                                        data-testid="rent-discovery-caption"
                                        className="mt-5 text-[11px] text-[#0A1F44]/55 leading-relaxed"
                                    >
                                        Estimated using a typical buyer profile.
                                        Personalise your numbers in the Affordability Assessment.
                                    </p>
                                </Reveal>

                                <Reveal delay={0.45}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            seedAssessmentFromLanding();
                                            setActiveSection("affordability");
                                        }}
                                        data-testid="rent-discovery-cta"
                                        className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-[#0A1F44] text-white px-6 py-4 rounded-full text-sm font-semibold hover:bg-[#1E6BFF] transition-colors group"
                                    >
                                        Continue to my full HomeIQ plan
                                        <span className="transition-transform group-hover:translate-x-1">
                                            →
                                        </span>
                                    </button>
                                </Reveal>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
}

function Reveal({ children, delay = 0 }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                duration: 0.6,
                delay,
                ease: [0.16, 1, 0.3, 1],
            }}
        >
            {children}
        </motion.div>
    );
}
